import { useState } from "react";
import { BadgeCheck, MessageCircle, Download, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, customerWaLink } from "@/lib/api";

export default function ConfirmCardButton({ enquiry, onSent, compact = false }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(enquiry.pickup_note || "");
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  if (enquiry.status !== "confirmed") return null;
  const id8 = enquiry.id.slice(0, 8);

  const load = async () => {
    setBusy(true);
    try {
      if (note !== (enquiry.pickup_note || "")) await api.patch(`/admin/enquiries/${enquiry.id}`, { pickup_note: note });
      const { data } = await api.get(`/admin/enquiries/${enquiry.id}/confirm-link`);
      setCard({ ...data, image_url: `${data.image_url}?v=${Date.now()}` });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build the card");
    } finally {
      setBusy(false);
    }
  };

  const markSent = async () => {
    await api.patch(`/admin/enquiries/${enquiry.id}`, { confirmation_sent: true, pickup_note: note }).catch(() => {});
    onSent?.(enquiry.id);
  };

  const sendWhatsApp = async () => {
    window.open(customerWaLink(enquiry.phone, card.message), "_blank");
    await markSent();
    toast.success("WhatsApp opened — confirmation marked as sent");
    setOpen(false);
  };

  const download = async () => {
    const a = document.createElement("a");
    a.href = card.image_url;
    a.download = `random-rider-confirmation-${id8}.jpg`;
    a.target = "_blank";
    a.click();
    await markSent();
    toast.success("Card downloaded — attach it in WhatsApp");
  };

  const openDialog = () => { setOpen(true); if (!card) load(); };

  return (
    <>
      <button onClick={openDialog} data-testid={`confirm-card-open-${id8}`}
        className={`flex items-center gap-1.5 font-semibold rounded-full transition-all ${enquiry.confirmation_sent ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-ink text-sand hover:bg-terra"} ${compact ? "text-[10px] px-2.5 py-1" : "text-xs px-3.5 py-1.5"}`}>
        <BadgeCheck size={compact ? 11 : 13} /> {enquiry.confirmation_sent ? "Confirmation sent · resend" : "Send confirmation card"}
      </button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-2xl bg-sand" data-testid="confirm-card-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-ink">Confirmation card · {enquiry.name}</DialogTitle></DialogHeader>
          <label className="block text-xs font-mono uppercase tracking-widest text-ink/50">Pickup note (shown on card)</label>
          <div className="flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={160} placeholder="e.g. Pickup 9 AM at Puri Railway Station, gate 1"
              data-testid="confirm-card-pickup-input" className="flex-1 bg-white border border-ink/15 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-terra" />
            <button onClick={load} disabled={busy} data-testid="confirm-card-refresh" className="text-xs font-semibold px-4 rounded-xl border border-ink/15 hover:bg-ink hover:text-sand transition-colors disabled:opacity-50">
              {busy ? "Building…" : "Update card"}
            </button>
          </div>
          <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink aspect-[1200/630] flex items-center justify-center">
            {card ? <img src={card.image_url} alt="Confirmation card preview" className="w-full h-full object-cover" data-testid="confirm-card-preview" />
              : <span className="text-sand/60 text-sm flex items-center gap-2"><ImageIcon size={16} /> {busy ? "Rendering your card…" : "Card preview"}</span>}
          </div>
          {card && (
            <p className="text-xs text-ink/60 break-all" data-testid="confirm-card-share-url">Card link: <a href={card.share_url} target="_blank" rel="noreferrer" className="text-terra underline">{card.share_url}</a></p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={sendWhatsApp} disabled={!card} data-testid="confirm-card-whatsapp"
              className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white text-sm font-semibold py-3 rounded-full hover:brightness-110 transition-all disabled:opacity-50">
              <MessageCircle size={15} /> Open WhatsApp with card link
            </button>
            <button onClick={download} disabled={!card} data-testid="confirm-card-download"
              className="flex items-center justify-center gap-2 border border-ink/15 text-ink text-sm font-semibold px-5 py-3 rounded-full hover:bg-ink hover:text-sand transition-colors disabled:opacity-50">
              <Download size={15} /> Download JPG
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
